import { useMemo } from "react";
import { useSelector } from "react-redux";
import AppState from "store/AppState";
import {
  selectProductColors,
  selectProductLeadingImage,
} from "store/product/ProductSelectors";
import { selectLeadingColorImageForProductInCategory } from "store/color-management/ColorManagementSelectors";
import { selectLeadingImageUrlForProductInCategory } from "store/leading-image/LeadingImageSelectors";
import { selectCurrentCategory } from "store/category/CategorySelectors";
import {
  getProductLeadingColorImageSFCC,
  getProductLeadingImageOnInitialLoad,
} from "utils/ProductUtil";
import { Product } from "store/product/ProductTypes";
import { FreqBoughtProductData } from "store/recommendations/RecommendationType";
import { ColorManagementProductData } from "store/color-management/ColorManagementTypes";

export const useProductImage = (
  productId: string,
  product?: Product | FreqBoughtProductData | ColorManagementProductData | null,
  categoryId?: string,
): string | undefined => {
  // Get current category for category-specific images
  const currentCategoryId = useSelector(selectCurrentCategory) || "";
  const effectiveCategoryId = categoryId || currentCategoryId;

  const productsColorData = useSelector(selectProductColors);
  const colors = productId ? productsColorData[productId]?.colors : undefined;
  const productLeadingImage = useSelector((state: AppState) =>
    selectProductLeadingImage(state, productId, effectiveCategoryId),
  );

  const leadingImageUrl =
    useSelector((state: AppState) =>
      selectLeadingImageUrlForProductInCategory(
        state,
        productId,
        effectiveCategoryId,
      ),
    ) ?? "";

  const updatedBaseProductLeadingColorImage = useSelector((state: AppState) =>
    selectLeadingColorImageForProductInCategory(
      state,
      productId,
      effectiveCategoryId,
    ),
  );

  const productLeadingImageOnInitialLoad = useMemo(() => {
    if (!product) return "";
    const leadingImage = getProductLeadingImageOnInitialLoad(
      product as Product | FreqBoughtProductData | ColorManagementProductData,
      effectiveCategoryId,
    );
    return leadingImage;
  }, [product, effectiveCategoryId]);

  const productLeadingColorImage = useMemo(() => {
    if (!product) return "";
    if ((product as any)?.categorySpecific) {
      return (product as any)?.categorySpecific?.length > 0
        ? getProductLeadingColorImageSFCC(product, effectiveCategoryId)
        : "";
    } else {
      return updatedBaseProductLeadingColorImage;
    }
  }, [product, effectiveCategoryId, updatedBaseProductLeadingColorImage]);

  const productImage = useMemo(() => {
    const image = leadingImageUrl
      ? leadingImageUrl
      : productLeadingImage
        ? productLeadingImage
        : productLeadingImageOnInitialLoad
          ? productLeadingImageOnInitialLoad
          : productLeadingColorImage
            ? productLeadingColorImage
            : colors?.find((color) => color.isPublished)?.thumbnail
              ? colors.find((color) => color.isPublished)?.thumbnail
              : product?.thumbnail;
    return image;
  }, [
    product,
    leadingImageUrl,
    productLeadingImage,
    productLeadingImageOnInitialLoad,
    productLeadingColorImage,
    colors,
  ]);

  return productImage;
};
